import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Play, Pause, X, Volume2, VolumeX, RotateCcw, ChevronLeft, ChevronRight } from 'lucide-react';
import { createMeditationAudio } from '../../utils/audioEngine';

const MeditationPlayer = ({ config, taskTitle, onComplete, onClose }) => {
  const { steps, total_duration_seconds, color } = config;
  const totalDuration = total_duration_seconds || steps.reduce((s, st) => s + st.duration, 0);

  const [elapsed, setElapsed] = useState(0);
  const [running, setRunning] = useState(false);
  const [started, setStarted] = useState(false);
  const [finished, setFinished] = useState(false);
  const [audioOn, setAudioOn] = useState(false);
  const intervalRef = useRef(null);
  const audioRef = useRef(null);

  // Compute current step
  let stepIdx = 0;
  let timeInStep = elapsed;
  for (let i = 0; i < steps.length; i++) {
    if (timeInStep < steps[i].duration) { stepIdx = i; break; }
    timeInStep -= steps[i].duration;
    if (i === steps.length - 1) stepIdx = steps.length - 1;
  }
  const currentStep = steps[stepIdx];
  const totalProgress = Math.min(elapsed / totalDuration, 1);
  const timeLeft = Math.max(totalDuration - elapsed, 0);

  useEffect(() => {
    if (!running) return;
    intervalRef.current = setInterval(() => {
      setElapsed(prev => {
        const next = prev + 1;
        if (next >= totalDuration) {
          clearInterval(intervalRef.current);
          setRunning(false);
          setFinished(true);
          return totalDuration;
        }
        return next;
      });
    }, 1000);
    return () => clearInterval(intervalRef.current);
  }, [running, totalDuration]);

  useEffect(() => {
    return () => { if (audioRef.current) audioRef.current.stop(); };
  }, []);

  const toggleAudio = () => {
    if (audioOn) {
      if (audioRef.current) audioRef.current.stop();
      audioRef.current = null;
      setAudioOn(false);
    } else {
      try {
        const engine = createMeditationAudio();
        engine.start();
        audioRef.current = engine;
        setAudioOn(true);
      } catch {}
    }
  };

  const handleStart = () => {
    setStarted(true);
    setRunning(true);
    // Auto-start ambient audio
    if (!audioRef.current) {
      try {
        const engine = createMeditationAudio();
        engine.start();
        audioRef.current = engine;
        setAudioOn(true);
      } catch {}
    }
  };

  const handleReset = () => {
    clearInterval(intervalRef.current);
    if (audioRef.current) { audioRef.current.stop(); audioRef.current = null; }
    setAudioOn(false);
    setElapsed(0); setRunning(false); setStarted(false); setFinished(false);
  };

  const handleFinish = useCallback(() => {
    if (audioRef.current) { audioRef.current.stop(); audioRef.current = null; }
    onComplete({
      started: true, duration_played: elapsed, total_duration: totalDuration,
      steps_completed: stepIdx + 1, total_steps: steps.length, task_type: 'guided_meditation',
    });
  }, [elapsed, totalDuration, stepIdx, steps.length, onComplete]);

  const fmt = (s) => `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, '0')}`;

  return (
    <div className="flex flex-col items-center justify-center h-full w-full px-6 select-none" data-testid="meditation-player">
      <div className="absolute top-0 left-0 right-0 flex items-center justify-between px-6 py-4 z-10">
        <button onClick={onClose} className="p-2 rounded-full hover:bg-white/5 transition">
          <X className="w-5 h-5 text-white/40" />
        </button>
        <span className="text-white/30 text-xs font-medium tracking-wider uppercase">{taskTitle}</span>
        <div className="w-9" />
      </div>

      {!started && !finished ? (
        <div className="flex flex-col items-center gap-8 animate-fadeIn">
          <div className="relative w-36 h-36 flex items-center justify-center">
            <div className="absolute inset-0 rounded-full animate-pulse" style={{
              background: `radial-gradient(circle, ${color}20, transparent 70%)`,
            }} />
            <div className="w-24 h-24 rounded-full" style={{
              background: `radial-gradient(circle, ${color}35, ${color}08)`,
              boxShadow: `0 0 50px ${color}25`,
            }} />
          </div>
          <div className="text-center space-y-3">
            <h2 className="text-white text-2xl font-bold">{taskTitle}</h2>
            <p className="text-white/40 text-sm">{fmt(totalDuration)} rehberli meditasyon</p>
          </div>
          <button onClick={handleStart}
            className="px-10 py-4 rounded-2xl text-base font-bold text-white transition-all hover:scale-105 active:scale-95"
            style={{ background: `linear-gradient(135deg, ${color}, ${color}BB)`, boxShadow: `0 12px 40px ${color}40` }}
            data-testid="meditation-start">
            Başla
          </button>
        </div>
      ) : finished ? (
        <div className="flex flex-col items-center gap-8 animate-fadeIn">
          <div className="w-28 h-28 rounded-full flex items-center justify-center" style={{ background: 'rgba(52,211,153,0.15)', boxShadow: '0 0 60px rgba(52,211,153,0.2)' }}>
            <svg className="w-14 h-14 text-emerald-400" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <div className="text-center space-y-2">
            <h2 className="text-white text-2xl font-bold">Meditasyon Tamamlandı</h2>
            <p className="text-white/40 text-sm">{fmt(totalDuration)} boyunca nefesine odaklandın</p>
          </div>
          <div className="flex gap-3">
            <button onClick={handleReset} className="flex items-center gap-2 px-5 py-3 rounded-xl text-sm font-medium text-white/50 hover:text-white/70 transition" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)' }}>
              <RotateCcw className="w-4 h-4" /> Tekrarla
            </button>
            <button onClick={handleFinish} className="px-8 py-3 rounded-xl text-sm font-bold text-white transition-all hover:scale-105" style={{ background: 'linear-gradient(135deg, #34D399, #34D399BB)', boxShadow: '0 8px 30px rgba(52,211,153,0.3)' }} data-testid="meditation-finish">
              Devam Et
            </button>
          </div>
        </div>
      ) : (
        <div className="flex flex-col items-center w-full max-w-md gap-6">
          <div className="text-white/20 text-xs tabular-nums tracking-widest">{fmt(timeLeft)} kaldı</div>

          <div className="min-h-[120px] flex items-center justify-center px-4">
            <p key={stepIdx}
              className="text-white/80 text-lg sm:text-xl text-center font-light leading-relaxed animate-fadeIn"
              style={{ maxWidth: 360 }}>
              {currentStep.text}
            </p>
          </div>

          {/* Step indicator + manual nav */}
          <div className="flex items-center gap-3">
            <button
              onClick={() => {
                if (stepIdx > 0) {
                  let t = 0;
                  for (let i = 0; i < stepIdx - 1; i++) t += steps[i].duration;
                  setElapsed(t);
                }
              }}
              disabled={stepIdx === 0}
              className="w-9 h-9 rounded-full flex items-center justify-center transition-all disabled:opacity-20"
              style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)' }}
              data-testid="meditation-prev-step">
              <ChevronLeft className="w-4 h-4 text-white/60" />
            </button>
            <div className="flex items-center gap-1.5">
              {steps.map((_, i) => (
                <div key={i} className="rounded-full transition-all duration-500" style={{
                  width: i === stepIdx ? 16 : 6, height: 6,
                  background: i <= stepIdx ? color : 'rgba(255,255,255,0.08)',
                  boxShadow: i === stepIdx ? `0 0 8px ${color}60` : 'none',
                }} />
              ))}
            </div>
            <button
              onClick={() => {
                if (stepIdx < steps.length - 1) {
                  let t = 0;
                  for (let i = 0; i <= stepIdx; i++) t += steps[i].duration;
                  setElapsed(t);
                }
              }}
              disabled={stepIdx === steps.length - 1}
              className="w-9 h-9 rounded-full flex items-center justify-center transition-all disabled:opacity-20"
              style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)' }}
              data-testid="meditation-next-step">
              <ChevronRight className="w-4 h-4 text-white/60" />
            </button>
          </div>

          <div className="w-full h-1 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.05)' }}>
            <div className="h-full rounded-full transition-all duration-1000 ease-linear" style={{
              width: `${totalProgress * 100}%`,
              background: `linear-gradient(90deg, ${color}, ${color}CC)`,
            }} />
          </div>

          <div className="flex items-center gap-4 mt-2">
            <button onClick={toggleAudio} className="w-10 h-10 rounded-full flex items-center justify-center transition-all" style={{
              background: audioOn ? `${color}20` : 'rgba(255,255,255,0.04)',
              border: `1px solid ${audioOn ? `${color}30` : 'rgba(255,255,255,0.06)'}`,
            }} data-testid="meditation-audio-toggle">
              {audioOn ? <Volume2 className="w-4 h-4" style={{ color }} /> : <VolumeX className="w-4 h-4 text-white/30" />}
            </button>
            <button onClick={running ? () => setRunning(false) : () => setRunning(true)}
              className="w-14 h-14 rounded-full flex items-center justify-center transition-all hover:scale-105"
              style={{ background: `${color}20`, border: `1px solid ${color}30`, boxShadow: `0 0 20px ${color}15` }}
              data-testid="meditation-pause-play">
              {running ? <Pause className="w-6 h-6" style={{ color }} /> : <Play className="w-6 h-6 ml-0.5" style={{ color }} />}
            </button>
            <div className="w-10" />
          </div>
        </div>
      )}
    </div>
  );
};

export default MeditationPlayer;
